import { privateApi } from '@/config/axios.config';
import { Provider } from '../types/provider.types';

const API_BASE = '/condominium/providers';

export interface ProviderCreateDTO {
  name: string;
  email: string;
  phone?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zipCode?: string;
  };
  type: ProviderType;
  category?: string;
  status?: ProviderStatus;
  contractStartDate?: string;
  contractEndDate?: string;
  contractDocument?: string;
  paymentTerms?: {
    paymentMethod?: string;
    paymentPeriod?: string;
    terms?: string;
    [key: string]: any;
  };
  rating?: number;
  metadata?: Record<string, any>;
}

export interface ProviderUpdateDTO extends Partial<ProviderCreateDTO> {
  id?: string;
}

export enum ProviderType {
  MAINTENANCE = 'maintenance',
  CLEANING = 'cleaning',
  SECURITY = 'security',
  LANDSCAPING = 'landscaping',
  POOL = 'pool',
  OTHER = 'other',
}

export enum ProviderStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
}

export interface ProviderFilters {
  type?: ProviderType;
  status?: ProviderStatus;
  search?: string;
  page?: number;
  limit?: number;
}

export const providersApiService = {
  // Obtener todos los proveedores con filtros
  async getProviders(filters: ProviderFilters = {}): Promise<{
    providers: Provider[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();

    if (filters.type) params.append('type', filters.type);
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await privateApi.get(`${API_BASE}?${params.toString()}`);
    return response.data?.data || response.data;
  },

  // Obtener un proveedor por ID
  async getProviderById(id: string): Promise<Provider> {
    const response = await privateApi.get(`${API_BASE}/${id}`);
    return response.data?.data || response.data;
  },

  // Crear un nuevo proveedor
  async createProvider(data: ProviderCreateDTO): Promise<Provider> {
    const response = await privateApi.post(API_BASE, data);
    return response.data?.data || response.data;
  },

  // Actualizar un proveedor
  async updateProvider(id: string, data: ProviderUpdateDTO): Promise<Provider> {
    const response = await privateApi.patch(`${API_BASE}/${id}`, data);
    return response.data?.data || response.data;
  },

  // Eliminar un proveedor
  async deleteProvider(id: string): Promise<void> {
    await privateApi.delete(`${API_BASE}/${id}`);
  },

  // Obtener historial de trabajos de un proveedor
  async getWorkHistory(id: string, filters?: { dateFrom?: string; dateTo?: string }): Promise<{
    works: Array<{
      id: string;
      maintenanceRequestId: string;
      workDate: string;
      cost: number;
      rating?: number;
      notes?: string;
    }>;
    total: number;
  }> {
    const params = new URLSearchParams();
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);

    const response = await privateApi.get(`${API_BASE}/${id}/work-history?${params.toString()}`);
    return response.data?.data || response.data;
  },
};

